import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, setToken, getToken, formatApiError, syncActiveBranch } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // { user, tenant, branches, permissions }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bootstrap = useCallback(async () => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (!data.user?.is_super_admin) syncActiveBranch(data.branches, data.user?.branch_id);
      setSession({
        user: data.user,
        tenant: data.tenant,
        branches: data.branches || [],
        permissions: data.permissions || [],
      });
    } catch {
      setToken(null);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      if (!data.user?.is_super_admin) syncActiveBranch(data.branches, data.user?.branch_id);
      setSession({
        user: data.user,
        tenant: data.tenant,
        branches: data.branches || [],
        permissions: data.permissions || [],
      });
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setToken(null);
    setSession(null);
  }, []);

  const hasPermission = useCallback(
    (key) => {
      if (!session) return false;
      if (session.user?.is_super_admin) return true;
      return (session.permissions || []).includes(key);
    },
    [session]
  );

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!data.user?.is_super_admin) syncActiveBranch(data.branches, data.user?.branch_id);
      setSession({
        user: data.user,
        tenant: data.tenant,
        branches: data.branches || [],
        permissions: data.permissions || [],
      });
    } catch (_) {}
  }, []);

  const value = useMemo(
    () => ({ session, loading, error, login, logout, hasPermission, refresh }),
    [session, loading, error, login, logout, hasPermission, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
